// Corpus-free category definitions — safe for client bundles.
export type VisaFaqCategory =
  | "visa-vs-permission"
  | "tourist"
  | "work-permit"
  | "extension"
  | "reporting"
  | "retirement"
  | "family"
  | "long-term"
  | "student"
  | "documents"
  | "refusal-overstay"
  | "outbound";

export interface VisaCategoryDef {
  slug: VisaFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const VISA_FAQ_CATEGORIES: VisaCategoryDef[] = [
  {
    slug: "visa-vs-permission",
    titleTh: "วีซ่า กับ การอนุญาตให้อยู่ต่อ ต่างกันอย่างไร",
    titleEn: "Visa vs Permission to Stay",
    descTh:
      "แยกให้ชัดระหว่างวีซ่าที่สถานทูตออก การอนุญาตให้เข้าเมืองที่ด่านตรวจคนเข้าเมือง และการอนุญาตให้อยู่ต่อในไทย",
    descEn:
      "The difference between a visa issued abroad, permission to enter granted at the border, and permission to stay extended inside Thailand.",
    matchTh: /(วีซ่าไม่ใช่|อนุญาตให้อยู่ต่อ|permission to stay|ตราประทับ|ด่านตรวจคนเข้าเมือง|วันหมดอายุวีซ่า)/i,
    matchEn: /(permission to (stay|enter)|entry stamp|visa validity|border officer|not a guarantee)/i,
  },
  {
    slug: "tourist",
    titleTh: "ท่องเที่ยว ผ.30 และ e-Visa",
    titleEn: "Tourist, Visa Exemption & e-Visa",
    descTh: "คำถามเรื่องวีซ่าท่องเที่ยว TR การยกเว้นวีซ่า Visa on Arrival และการยื่นผ่านระบบ e-Visa",
    descEn: "Tourist visa (TR), visa exemption, visa on arrival and the Thai e-Visa system.",
    matchTh: /(ท่องเที่ยว|ยกเว้นวีซ่า|visa exempt|visa on arrival|e-?visa|METV|TDAC|บัตรขาเข้า)/i,
    matchEn: /(tourist|visa exempt|visa on arrival|e-?visa|METV|arrival card)/i,
  },
  {
    slug: "work-permit",
    titleTh: "ทำงานในไทย Non-B และใบอนุญาตทำงาน",
    titleEn: "Working in Thailand: Non-B & Work Permit",
    descTh: "วีซ่าธุรกิจ Non-B ใบอนุญาตทำงาน แบบ ตท. และ WP และเงื่อนไขนายจ้างที่มักถูกตรวจ",
    descEn: "Non-B business visa, work permit forms, and the employer conditions most often checked.",
    matchTh: /(ใบอนุญาตทำงาน|work permit|non-?b|ตท\.|WP\.|กรมการจัดหางาน|นายจ้าง|ครูสอน)/i,
    matchEn: /(work permit|non-?b|department of employment|employer|teaching)/i,
  },
  {
    slug: "extension",
    titleTh: "ขออยู่ต่อและเปลี่ยนประเภทวีซ่าในไทย",
    titleEn: "Extension of Stay & Change of Visa Type",
    descTh: "การยื่น ตม.7 ขออยู่ต่อ การเปลี่ยนประเภทการตรวจลงตราในไทย และเอกสารที่ต้องเตรียม",
    descEn: "Filing TM.7 for extension of stay, changing visa category inside Thailand, and required documents.",
    matchTh: /(อยู่ต่อ|ตม\.7|TM\.?7|ต่ออายุ|เปลี่ยนประเภทวีซ่า|1,?900)/i,
    matchEn: /(extension of stay|TM\.?7|change of visa|1,?900)/i,
  },
  {
    slug: "reporting",
    titleTh: "รายงานตัว 90 วัน TM30 และ Re-entry Permit",
    titleEn: "90-Day Report, TM30 & Re-entry Permit",
    descTh: "หน้าที่หลังได้รับอนุญาตให้อยู่ต่อ ทั้งรายงานตัว 90 วัน การแจ้งที่พักโดยเจ้าบ้าน และใบอนุญาตกลับเข้ามาในไทย",
    descEn: "Obligations after your stay is approved: 90-day reporting, TM30 address notification, and re-entry permits.",
    matchTh: /(90 วัน|ตม\.47|TM\.?47|TM\s?30|ตม\.30|แจ้งที่พัก|re-?entry|เจ้าบ้าน)/i,
    matchEn: /(90-?day|TM\.?47|TM\s?30|address notification|re-?entry)/i,
  },
  {
    slug: "retirement",
    titleTh: "วีซ่าเกษียณอายุ Non-O Non-OA Non-OX",
    titleEn: "Retirement Visas: Non-O, O-A, O-X",
    descTh: "เกณฑ์อายุ เงินฝากในบัญชีไทย รายได้ต่อเดือน และประกันสุขภาพที่ใช้กับวีซ่าเกษียณแต่ละแบบ",
    descEn: "Age, Thai bank deposit, monthly income and health-insurance conditions for each retirement route.",
    matchTh: /(เกษียณ|retirement|O-?A|O-?X|800,?000|65,?000|ประกันสุขภาพ)/i,
    matchEn: /(retirement|O-?A|O-?X|800,?000|65,?000|health insurance)/i,
  },
  {
    slug: "family",
    titleTh: "วีซ่าครอบครัว คู่สมรส และผู้ติดตาม",
    titleEn: "Family, Spouse & Dependent Visas",
    descTh: "Non-O สมรสกับคนไทย ผู้ติดตามของผู้ถือ Non-B และเอกสารความสัมพันธ์ที่ต้องแปลและรับรอง",
    descEn: "Non-O based on Thai marriage, dependents of Non-B holders, and relationship documents that need translation.",
    matchTh: /(สมรส|คู่สมรส|ผู้ติดตาม|บุตร|400,?000|40,?000|dependent)/i,
    matchEn: /(marriage|spouse|dependent|child|400,?000|40,?000)/i,
  },
  {
    slug: "long-term",
    titleTh: "วีซ่าระยะยาว DTV LTR SMART และ Thailand Privilege",
    titleEn: "Long-Stay Options: DTV, LTR, SMART, Thailand Privilege",
    descTh: "เปรียบเทียบทางเลือกพำนักระยะยาว หน่วยงานที่พิจารณา และสิทธิทำงานของแต่ละประเภท",
    descEn: "Comparing long-stay options, the deciding authority, and work rights attached to each.",
    matchTh: /(DTV|LTR|SMART|Thailand Privilege|Elite|180 วัน|BOI)/i,
    matchEn: /(DTV|LTR|SMART|Thailand Privilege|Elite|180 days|BOI)/i,
  },
  {
    slug: "student",
    titleTh: "วีซ่านักเรียนและฝึกอบรม Non-ED",
    titleEn: "Student & Training Visas (Non-ED)",
    descTh: "หนังสือตอบรับจากสถานศึกษา การอยู่ต่อเพื่อการศึกษา และข้อจำกัดเรื่องการทำงาน",
    descEn: "Acceptance letters, education-based extensions, and the limits on working while studying.",
    matchTh: /(นักเรียน|นักศึกษา|Non-?ED|สถานศึกษา|หลักสูตร|ฝึกอบรม)/i,
    matchEn: /(student|Non-?ED|school|course|training|university)/i,
  },
  {
    slug: "documents",
    titleTh: "เอกสาร คำแปล และการรับรองสำหรับงานวีซ่า",
    titleEn: "Documents, Translation & Legalization for Visas",
    descTh: "เอกสารไทยและต่างประเทศที่ต้องแปลและรับรองก่อนยื่นวีซ่า รวมถึงหนังสือรับรองความประพฤติ",
    descEn: "Thai and foreign documents that need translation and legalization before filing, including police clearance.",
    matchTh: /(แปล|รับรองคำแปล|กงสุล|apostille|หนังสือรับรองความประพฤติ|ทะเบียนสมรส|สูติบัตร)/i,
    matchEn: /(translat|legaliz|apostille|police clearance|marriage certificate|birth certificate)/i,
  },
  {
    slug: "refusal-overstay",
    titleTh: "ถูกปฏิเสธ อยู่เกินกำหนด และการยื่นใหม่",
    titleEn: "Refusals, Overstay & Re-applying",
    descTh: "สาเหตุการปฏิเสธที่พบบ่อย ผลของการอยู่เกินกำหนด และแนวทางเตรียมเอกสารเมื่อยื่นใหม่",
    descEn: "Common refusal reasons, consequences of overstaying, and how to prepare a stronger re-application.",
    matchTh: /(ปฏิเสธ|overstay|อยู่เกิน|blacklist|ยื่นใหม่|อุทธรณ์)/i,
    matchEn: /(refus|overstay|blacklist|re-?appl|appeal)/i,
  },
  {
    slug: "outbound",
    titleTh: "คนไทยยื่นวีซ่าไปต่างประเทศ",
    titleEn: "Thai Nationals Applying Abroad",
    descTh: "เชงเก้น อังกฤษ สหรัฐฯ ออสเตรเลีย แคนาดา ญี่ปุ่น เกาหลี พร้อมเอกสารการเงินและคำแปลที่ปลายทางยอมรับ",
    descEn: "Schengen, UK, US, Australia, Canada, Japan and Korea — financial evidence and accepted translations.",
    matchTh: /(เชงเก้น|Schengen|อังกฤษ|สหรัฐ|ออสเตรเลีย|แคนาดา|ญี่ปุ่น|เกาหลี|VFS|DS-160|NAATI)/i,
    matchEn: /(Schengen|United Kingdom|United States|Australia|Canada|Japan|Korea|VFS|DS-160|NAATI)/i,
  },
];

export function getVisaCategoryDef(slug: string): VisaCategoryDef | undefined {
  return VISA_FAQ_CATEGORIES.find((c) => c.slug === slug);
}